import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingChat from "@/components/FloatingChat";
import ScheduleDialog from "@/components/ScheduleDialog";
import { ScheduleProvider, useSchedule } from "@/hooks/use-schedule";
import { Button } from "@/components/ui/button";
import { Calendar, MessageCircle, CheckCircle, Monitor, Clock, Globe, FileText, MessageSquare, Utensils } from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import heroBackground from "@/assets/hero-background.jpg";
import claraPortrait from "@/assets/clara-portrait-final.png";

const comoFunciona = [
  { step: "1", title: "Você agenda pelo Calendly ou WhatsApp", desc: "Escolha o horário que funciona melhor para você. A consulta é por vídeo, de qualquer lugar." },
  { step: "2", title: "Conversamos por videochamada", desc: "Uma conversa tranquila, em que conheço sua rotina, seus objetivos e o que você já tentou antes." },
  { step: "3", title: "Receba seu plano alimentar personalizado", desc: "Montado com os produtos que você encontra nos mercados australianos, respeitando seus gostos e horários." },
  { step: "4", title: "Acompanhamento contínuo entre consultas", desc: "Acesso à plataforma, suporte por WhatsApp e ajustes sempre que precisar." },
];

const paraQuem = [
  { icon: Globe, title: "Brasileiros em qualquer cidade da Austrália", desc: "Sydney, Melbourne, Perth, Adelaide, Gold Coast ou qualquer outra cidade." },
  { icon: Clock, title: "Quem tem rotina corrida e precisa de praticidade", desc: "Sem precisar sair de casa ou perder tempo no trânsito." },
  { icon: MessageSquare, title: "Quem quer atendimento em português", desc: "Sem barreiras de idioma, com quem entende a cultura brasileira." },
  { icon: Utensils, title: "Quem quer emagrecer ou melhorar a alimentação", desc: "Com um plano que funciona na sua vida real, não numa vida ideal." },
];

const oQueInclui = [
  "Consulta por videochamada com duração adequada ao seu caso",
  "Plano alimentar personalizado com produtos australianos",
  "Acesso à plataforma com conteúdo de apoio",
  "Suporte por WhatsApp entre as consultas",
  "Ajustes no plano conforme sua rotina muda",
  "Acompanhamento de exames e evolução",
];

export const faqItems = [
  { question: "Preciso instalar algum programa para a consulta online?", answer: "Não. A consulta é feita por videochamada, por um link que eu envio para você. Funciona no celular, no tablet ou no computador." },
  { question: "A consulta online é tão boa quanto a presencial?", answer: "Sim. A qualidade do acompanhamento é a mesma. A única diferença é que você não precisa sair de casa." },
  { question: "Quanto tempo dura a consulta?", answer: "A consulta inicial dura cerca de 30 minutos e não tem custo. As consultas de acompanhamento variam de acordo com o que precisamos trabalhar." },
  { question: "Posso fazer a consulta fora do horário comercial?", answer: "Sim. Tenho horários flexíveis justamente para atender quem trabalha em horários variados." },
  { question: "E se eu precisar remarcar?", answer: "Sem problema. Você pode remarcar pelo Calendly ou me avisar pelo WhatsApp." },
];

const CTAButtons = ({ openDialog }: { openDialog: () => void }) => (
  <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
    <Button
      size="lg"
      variant="gradient"
      className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
      style={{ background: 'var(--gradient-green)' }}
      onClick={openDialog}
    >
      <Calendar className="mr-2 h-5 w-5" />
      Agendar consulta
    </Button>
    <Button
      size="lg"
      variant="gradient-outline"
      className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 w-full sm:w-auto"
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--gradient-green)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
      onClick={() => window.open('https://wa.me/61423038977', '_blank')}
    >
      <MessageCircle className="mr-2 h-5 w-5" />
      Falar no WhatsApp
    </Button>
  </div>
);

const PageContent = () => {
  const { open, setOpen, openDialog } = useSchedule();

  return (
    <>

      <div className="min-h-screen">
        <Navigation />

        <main>
          {/* HERO */}
          <section className="relative overflow-hidden pt-24 sm:pt-32 pb-14 sm:pb-20">
            <div
              className="absolute inset-0 opacity-15"
              style={{
                backgroundImage: `url(${heroBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-background to-transparent z-[2]" />

            <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-4xl text-center space-y-6 sm:space-y-8">
              <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                Clara Camille · Nutricionista
              </p>
              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-typewriter font-bold text-foreground leading-tight">
                Consulta nutricional online na Austrália, em português
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground font-sans leading-relaxed max-w-2xl mx-auto">
                Atendimento por videochamada com plano alimentar personalizado. De qualquer cidade da Austrália, sem sair de casa.
              </p>
              <CTAButtons openDialog={openDialog} />
              <p className="text-sm text-muted-foreground font-sans">
                Consulta inicial de 30 minutos, sem custo.
              </p>
            </div>
          </section>

          {/* COMO FUNCIONA */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Passo a passo
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Como funciona a consulta online
                </h2>
              </div>

              <div className="space-y-6 sm:space-y-8">
                {comoFunciona.map((item) => (
                  <div key={item.step} className="flex gap-4 sm:gap-6 items-start">
                    <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-typewriter font-bold text-lg sm:text-xl text-primary-foreground" style={{ background: 'var(--gradient-green)' }}>
                      {item.step}
                    </div>
                    <div className="space-y-1 sm:space-y-2 pt-1">
                      <h3 className="text-base sm:text-xl font-bold text-foreground font-sans">{item.title}</h3>
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* PARA QUEM */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Para quem é
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Para quem é a consulta nutricional online
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-8">
                {paraQuem.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex gap-4 sm:gap-5 p-4 sm:p-6 rounded-xl sm:rounded-2xl bg-primary/5 hover:bg-primary/10 transition-colors">
                      <div className="flex-shrink-0">
                        <div className="w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-primary/15 flex items-center justify-center">
                          <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-primary" strokeWidth={1.5} />
                        </div>
                      </div>
                      <div className="space-y-1 sm:space-y-2">
                        <h3 className="text-base sm:text-xl font-bold text-foreground font-sans">{item.title}</h3>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-sans">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* O QUE ESTÁ INCLUSO */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  O que está incluído
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  O que você recebe com a consulta online
                </h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-3xl mx-auto">
                {oQueInclui.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-4 rounded-xl bg-primary/5">
                    <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-0.5" />
                    <p className="text-sm sm:text-base text-foreground font-sans leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10 sm:mt-14">
                <CTAButtons openDialog={openDialog} />
              </div>
            </div>
          </section>

          {/* QUEM TE ATENDE */}
          <section className="bg-background py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
              <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10 p-5 sm:p-8 rounded-2xl sm:rounded-3xl bg-primary/5">
                <div className="flex-shrink-0">
                  <img
                    src={claraPortrait}
                    alt="Clara Camille, nutricionista brasileira na Austrália"
                    className="w-28 h-28 sm:w-36 sm:h-36 rounded-full object-cover object-top shadow-lg"
                  />
                </div>
                <div className="text-center sm:text-left space-y-2 sm:space-y-3">
                  <h3 className="text-xl sm:text-2xl font-typewriter font-bold text-foreground">Clara Camille</h3>
                  <p className="text-sm sm:text-base text-primary font-typewriter font-semibold">Nutricionista · Brisbane, Austrália</p>
                  <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
                    Brasileira, nutricionista e imigrante. Entendo sua rotina porque vivo a mesma realidade. Atendo presencialmente em Brisbane e online para toda a Austrália.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="bg-card py-14 sm:py-20 lg:py-24">
            <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
              <div className="text-center mb-10 sm:mb-16 space-y-3">
                <p className="text-base sm:text-lg text-primary font-typewriter font-semibold uppercase tracking-wider">
                  Dúvidas frequentes
                </p>
                <h2 className="text-2xl sm:text-4xl lg:text-5xl font-typewriter font-bold text-foreground">
                  Perguntas sobre a consulta online
                </h2>
              </div>

              <Accordion type="single" collapsible className="space-y-3">
                {faqItems.map((item, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    className="border border-border rounded-xl px-5 sm:px-6 bg-card"
                  >
                    <AccordionTrigger className="text-left text-sm sm:text-base font-sans font-semibold text-foreground hover:no-underline py-4 sm:py-5">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed pb-4 sm:pb-5">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {/* Internal link */}
              <div className="mt-10 text-center">
                <p className="text-sm text-muted-foreground font-sans">
                  Saiba mais sobre meu trabalho na página{" "}
                  <Link to="/nutricionista-brasileira-na-australia" className="gradient-text italic font-semibold hover:opacity-80 transition-opacity">
                    Nutricionista brasileira na Austrália
                  </Link>.
                </p>
              </div>
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="py-16 sm:py-24 lg:py-32 relative overflow-hidden">
            <div
              className="absolute inset-0 blur-sm scale-105"
              style={{
                backgroundImage: `url(${heroBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
            <div className="absolute inset-0 opacity-80" style={{ background: 'var(--gradient-green)' }} />

            <div className="container mx-auto px-4 sm:px-6 relative z-10 flex justify-center">
              <div
                className="max-w-2xl w-full text-center space-y-6 sm:space-y-8 rounded-2xl sm:rounded-3xl px-5 py-10 sm:px-8 sm:py-12 lg:px-14 lg:py-16"
                style={{
                  background: 'rgba(255, 255, 255, 0.12)',
                  backdropFilter: 'blur(24px) saturate(1.4)',
                  WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255,255,255,0.3)',
                }}
              >
                <h2 className="text-2xl sm:text-3xl lg:text-5xl font-typewriter font-bold text-white leading-tight">
                  Agende sua consulta online
                </h2>
                <p className="text-base sm:text-lg lg:text-xl text-white/90 font-sans leading-relaxed">
                  Na consulta inicial, conheço sua rotina e seus objetivos e explico como posso ajudar você a cuidar da alimentação aqui na Austrália.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center pt-2">
                  <Button
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 bg-white text-foreground hover:bg-white/90 font-semibold shadow-lg w-full sm:w-auto"
                    onClick={openDialog}
                  >
                    <Calendar className="mr-2 h-5 w-5" />
                    Agendar consulta
                  </Button>
                  <Button
                    size="lg"
                    className="rounded-full text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 font-semibold shadow-lg border-0 w-full sm:w-auto"
                    style={{ background: 'rgba(255, 255, 255, 0.2)', backdropFilter: 'blur(12px)', color: 'white' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
                    onClick={() => window.open('https://wa.me/61423038977', '_blank')}
                  >
                    <MessageCircle className="mr-2 h-5 w-5" />
                    Falar no WhatsApp
                  </Button>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <FloatingChat />
        <ScheduleDialog open={open} onOpenChange={setOpen} />
      </div>
    </>
  );
};

const ConsultaNutricionalOnline = () => (
  <ScheduleProvider>
    <PageContent />
  </ScheduleProvider>
);

export default ConsultaNutricionalOnline;
